// src/components/BookCard.tsx
import { Card, CardContent, CardMedia, Typography, Box, Button, IconButton } from "@mui/material";
import { motion } from "motion/react";
import { useEditMode } from "../context/EditModeContext";
import { Edit, Delete } from "@mui/icons-material";

interface BookCardProps {
  title: string;
  author: string;
  year: string;
  cover: string;
  link: string;
  onEdit?: () => void;
  onDelete?: () => void;
}

export default function BookCard({ title, author, year, cover, link, onEdit, onDelete }: BookCardProps) {
  const { isEditMode } = useEditMode();

  return (
    <motion.div
      whileHover={{ scale: 1.03, boxShadow: "0px 8px 20px rgba(0,0,0,0.15)" }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
      style={{ height: "100%" }}
    >
      {/* Edit/Delete Buttons */}
      {isEditMode && (onEdit || onDelete) && (
        <Box >
          {onEdit && (
            <IconButton size="small" onClick={onEdit}>
              <Edit fontSize="small" />
            </IconButton>
          )}
          {onDelete && (
            <IconButton size="small" onClick={onDelete}>
              <Delete fontSize="small" color="error" />
            </IconButton>
          )}
        </Box>
      )}
      <Card
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {/* Fixed Aspect Ratio Image */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            pt: "150%", // Aspect ratio height
            overflow: "hidden",
          }}
        >
          <CardMedia
            component="img"
            image={cover}
            alt={title}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </Box>

        {/* Content area */}
        <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          <Box>
            <Typography variant="h6" gutterBottom noWrap>
              {title}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary" noWrap>
              {author}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {year}
            </Typography>
          </Box>
        </CardContent>

        {/* Buy button at bottom */}
        <Box sx={{ p: 2, pt: 0 }}>
          <Button variant="contained" color="primary" fullWidth href={link} target="_blank">
            Buy Now
          </Button>
        </Box>
      </Card>
    </motion.div>
  );
}
